import { LoggerService } from '../logger.service';
import { DismissedNotification } from './models/dismissed-notifications';
import {
  DisplayedNotificationDetails,
  GameId,
} from './models/displayed-notifications';
import { NotificationHistory } from './models/notification-history';
import { LocalStorage } from 'app-core/src/utils/storage/local-storage';
import { RecommendationConfig } from '../datastore-service/models/recommendation-config.model';
import { Game } from '../../models/game.model';
import { singleton } from 'tsyringe';
import { Application } from '../../models/application.model';
import { ConditionsSummary } from 'app-core/src/services/notification-analytics/models/conditions-summary.model';
import { ExtensionId } from '../datastore-service/models/lookups.model';

const kNameOfNotificationHistory = 'notification-history';
const kNameOfGlobalConfigurations = 'notification-global-configurations';
const kNameOfLegacyLastNotificationTimestamp = 'LastNotificationTimestamp';
const kNameOfLegacyAppDisplayCountPrefix = 'AppsDisplayCount_';
const kNameOfLegacyDataMigrated = 'LegacyDataMigrated';
const kManuallyDismissedAppIds = 'DismissedApps';
const kNameOfConditionsSummary = 'notifications-conditions-summary';

type ApplicationDisplayCount = { [appId: string]: number };
// -----------------------------------------------------------------------------
@singleton()
export class PersistentStorageService {
  private readonly logger = LoggerService.getCategory(
    'PersistentStorageService',
  );

  private isMigrating: boolean;
  constructor() {
    this.isMigrating = false;
  }

  // ---------------------------------------------------------------------------
  public deleteLocalStorageItems() {
    localStorage.removeItem(kNameOfNotificationHistory);
    localStorage.removeItem(kNameOfGlobalConfigurations);
    localStorage.removeItem(kNameOfConditionsSummary);
  }

  // ---------------------------------------------------------------------------
  // Global configurations
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getGlobalConfiguration(): RecommendationConfig {
    const config = LocalStorage.get(
      kNameOfGlobalConfigurations,
    ).asObject() as RecommendationConfig;

    if (!config) {
      this.logger.error('Notification global configurations were not found');
      return null;
    }

    return config;
  }

  // ---------------------------------------------------------------------------
  public setGlobalConfiguration(config: RecommendationConfig) {
    LocalStorage.setObject(kNameOfGlobalConfigurations, config);
  }

  // ---------------------------------------------------------------------------
  // Last displayed notification timestamp
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getLastDisplayedNotificationTimestamp() {
    const notificationsHistory = this.getNotificationHistory();
    return notificationsHistory?.lastDisplayedTimestamp;
  }

  // ---------------------------------------------------------------------------
  public setLastDisplayedNotificationTimestamp(timestamp = Date.now()) {
    const notificationsHistory = this.getNotificationHistory();
    notificationsHistory.lastDisplayedTimestamp = timestamp;
    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  public resetLastDisplayedNotificationTimestamp() {
    const notificationsHistory = this.getNotificationHistory();
    notificationsHistory.lastDisplayedTimestamp = 0;
    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  // Dismissed notifications
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getDismissedNotifications() {
    const notificationsHistory = this.getNotificationHistory();
    return notificationsHistory?.dismissedApps;
  }

  // ---------------------------------------------------------------------------
  public setNotificationDismissed(applicationUid: string) {
    const notificationsHistory = this.getNotificationHistory();

    const dismissedNotification: DismissedNotification = {
      applicationId: applicationUid,
      timestamp: Date.now(),
    };

    if (!notificationsHistory.dismissedApps) {
      notificationsHistory.dismissedApps = [dismissedNotification];
    } else {
      notificationsHistory.dismissedApps.push(dismissedNotification);
    }

    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  public removeNotificationFromDismissedApps(application: Application) {
    const notificationsHistory = this.getNotificationHistory();

    notificationsHistory.dismissedApps =
      notificationsHistory.dismissedApps.filter((element) => {
        return element.applicationId !== application.uid;
      });

    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  // Displayed Notifications
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getDisplayedNotificationInfo(
    game: Game,
    application: Application,
  ): DisplayedNotificationDetails {
    const notificationsHistory = this.getNotificationHistory();

    const displayedApps = notificationsHistory.displayedAppsPerGame;
    if (!displayedApps) {
      return null;
    }

    const foundGame = displayedApps[game.id];

    if (!foundGame) {
      return null;
    }

    const appInfo = foundGame[application.uid];

    if (!appInfo) {
      return null;
    }

    return appInfo;
  }

  // ---------------------------------------------------------------------------
  // Excluded
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public addGameToExcluded(gameId: GameId) {
    const notificationsHistory = this.getNotificationHistory();
    if (!notificationsHistory.excluded) {
      notificationsHistory.excluded = { games: [] };
    }

    const excluded = notificationsHistory.excluded;

    if (!excluded.games) {
      excluded.games = [];
    }

    if (!excluded.games.includes(gameId)) {
      excluded.games = [...excluded.games, gameId];
    }

    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  public addExtensionToExcluded(extensionId: ExtensionId) {
    const notificationsHistory = this.getNotificationHistory();
    if (!notificationsHistory.excluded) {
      notificationsHistory.excluded = { applications: [] };
    }

    const excluded = notificationsHistory.excluded;

    if (!excluded.applications) {
      excluded.applications = [];
    }

    if (!excluded.applications.includes(extensionId)) {
      excluded.applications = [...excluded.applications, extensionId];
    }

    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  public getExcludedExtensions(): string[] {
    const notificationsHistory = this.getNotificationHistory();
    if (
      !notificationsHistory.excluded ||
      !notificationsHistory.excluded.applications
    ) {
      return [];
    }

    return notificationsHistory?.excluded.applications;
  }

  // ---------------------------------------------------------------------------
  public getExcludedGames(): GameId[] {
    const notificationsHistory = this.getNotificationHistory();
    if (
      !notificationsHistory.excluded ||
      !notificationsHistory.excluded.games
    ) {
      return [];
    }

    return notificationsHistory?.excluded.games;
  }

  // countOverride - Used during the migration flow, it overrides the count
  // increment, the count is stored in storage exactly as was before migration.
  // ---------------------------------------------------------------------------
  public setNotificationDisplayed(
    gameId: number,
    applicationUid: string,
    countOverride = 0,
  ) {
    const notificationsHistory = this.getNotificationHistory();

    let displayAppsPerGame = notificationsHistory.displayedAppsPerGame;
    if (!displayAppsPerGame) {
      notificationsHistory.displayedAppsPerGame = {};
      displayAppsPerGame = notificationsHistory.displayedAppsPerGame;
    }

    let displayedAppsForGame = displayAppsPerGame[gameId];
    if (!displayedAppsForGame) {
      notificationsHistory.displayedAppsPerGame[gameId] = {};
      displayedAppsForGame = notificationsHistory.displayedAppsPerGame[gameId];
    }

    if (!displayedAppsForGame[applicationUid]) {
      displayedAppsForGame[applicationUid] = {
        lastTimestamp: 0,
        count: 0,
      };
    }

    const displayDetails = displayedAppsForGame[applicationUid];
    displayDetails.lastTimestamp = Date.now();
    displayDetails.count =
      countOverride > 0 ? countOverride : displayDetails.count + 1;
    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  public resetDisplayedNotificationCount(game: Game, app: Application) {
    const notificationsHistory = this.getNotificationHistory();

    const displayedAppsForGame =
      notificationsHistory.displayedAppsPerGame[game.id];
    if (!displayedAppsForGame) {
      return;
    }

    if (displayedAppsForGame[app.uid]) {
      delete notificationsHistory.displayedAppsPerGame[game.id][app.uid];
    }

    this.setNotificationHistory(notificationsHistory);
  }

  // ---------------------------------------------------------------------------
  // analyticsConditionsSummary
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public setConditionsSummary(conditionsSummary: ConditionsSummary) {
    LocalStorage.setObject(kNameOfConditionsSummary, conditionsSummary);
  }

  // ---------------------------------------------------------------------------
  public getConditionsSummary() {
    return LocalStorage.get(
      kNameOfConditionsSummary,
    ).asObject() as ConditionsSummary;
  }

  // ---------------------------------------------------------------------------
  // NotificationHistory
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getNotificationHistory(): NotificationHistory {
    this.migrateLegacyLocalStorageData();

    const notificationHistory = LocalStorage.get(
      kNameOfNotificationHistory,
    ).asObject() as NotificationHistory;

    if (notificationHistory) {
      return notificationHistory;
    }

    return this.generateNewNotificationHistory();
  }

  // ---------------------------------------------------------------------------
  public createNotificationHistory() {
    const notificationHistory = this.generateNewNotificationHistory();
    this.setNotificationHistory(notificationHistory);
  }

  // ---------------------------------------------------------------------------
  private generateNewNotificationHistory(): NotificationHistory {
    return {
      lastDisplayedTimestamp: 0,
      dismissedApps: [],
      displayedAppsPerGame: {},
      excluded: {
        applications: [],
        games: [],
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Legacy Migration
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  private migrateLegacyLocalStorageData() {
    if (
      this.isLegacyDataMigrated() ||
      this.isMigrating ||
      !this.isMigrationNeeded()
    ) {
      return;
    }

    this.isMigrating = true;
    this.createNotificationHistory();
    this.migrateLegacyLastNotificationTimestamp();
    this.migrateLegacyAppDisplayCount();
    this.migrateLegacyDismissedApps();
    this.deleteLegacyKeys();
    this.setLegacyDataMigrated();
    this.isMigrating = false;
  }

  // ---------------------------------------------------------------------------
  private isMigrationNeeded() {
    const LegacyKey = LocalStorage.get('CachedNotificationsInfo');

    return !!LegacyKey.raw;
  }

  // ---------------------------------------------------------------------------
  private setNotificationHistory(notificationHistory: NotificationHistory) {
    LocalStorage.setObject(kNameOfNotificationHistory, notificationHistory);
  }

  // ---------------------------------------------------------------------------
  private migrateLegacyLastNotificationTimestamp() {
    const timestamp = LocalStorage.get(
      kNameOfLegacyLastNotificationTimestamp,
    ).asInt();

    if (!timestamp) {
      return;
    }

    LocalStorage.remove(kNameOfLegacyLastNotificationTimestamp);
    this.setLastDisplayedNotificationTimestamp(timestamp);
  }

  // ---------------------------------------------------------------------------
  private migrateLegacyAppDisplayCount() {
    const legacyDisplayCount = LocalStorage.getKeysByPrefix(
      kNameOfLegacyAppDisplayCountPrefix,
    );

    if (!legacyDisplayCount) {
      return;
    }

    legacyDisplayCount.forEach((key) => {
      const gameId = key.split('_').pop();
      const displayedApplicationsCount = LocalStorage.get(
        key,
      ).asObject() as ApplicationDisplayCount;

      const parsedDisplayedApplicationsCount: ApplicationDisplayCount =
        displayedApplicationsCount;

      Object.keys(parsedDisplayedApplicationsCount).forEach((app) => {
        const count = parsedDisplayedApplicationsCount[app];
        this.setLegacyNotificationDisplayed(parseInt(gameId), app, count);
      });

      LocalStorage.remove(key);
    });
  }

  // ---------------------------------------------------------------------------
  private setLegacyNotificationDisplayed(
    gameId: number,
    applicationUid: string,
    count: number,
  ) {
    this.setNotificationDisplayed(gameId, applicationUid, count);
  }

  // ---------------------------------------------------------------------------
  private migrateLegacyDismissedApps() {
    const dismissedApps = LocalStorage.get(kManuallyDismissedAppIds).asArray();
    if (!dismissedApps) {
      return;
    }

    dismissedApps.forEach((app) => {
      this.setLegacyDismissedApp(app);
    });

    LocalStorage.remove(kManuallyDismissedAppIds);
  }

  // ---------------------------------------------------------------------------
  private setLegacyDismissedApp(applicationUid: string) {
    this.setNotificationDismissed(applicationUid);
  }

  // ---------------------------------------------------------------------------
  private deleteLegacyKeys() {
    let legacyKeys = [
      'NotificationsInfoLastModified',
      'LastNotificationTimestamp',
      'LastNotificationGame',
      'DismissedApps',
      'CachedNotificationsInfo',
      'LastNotificationParameterIsVideo',
      'LastNotificationParameter',
    ];

    const legacyKeysWithPrefix = [
      'NotificationShowCount_',
      'LastPlayed_',
      'AppsDisplayCount_',
      'SendControl_',
    ];

    legacyKeysWithPrefix.forEach((key) => {
      const foundKeys = LocalStorage.getKeysByPrefix(key);
      legacyKeys = [...legacyKeys, ...foundKeys];
    });

    legacyKeys.forEach((key) => {
      LocalStorage.remove(key);
    });
  }

  // ---------------------------------------------------------------------------
  private isLegacyDataMigrated(): boolean {
    return LocalStorage.get(kNameOfLegacyDataMigrated).asBool();
  }

  // ---------------------------------------------------------------------------
  private setLegacyDataMigrated() {
    LocalStorage.setBool(kNameOfLegacyDataMigrated, true);
  }
}
